// Settings → Zmanim → Time Settings ("Round To") applied to a resolved zman.
//
// Each organization configures, per zman key, how that zman's seconds are
// folded into the shown minute — `round to high`, `round to closer` or
// `round to lower` (Zmanim_Display_Override_N.roundTo). The Zmanim boards
// already honour it; a Minyanim Roll minyan on Dynamic Zman or Weekly Static
// Zman resolves the very same zman, so it must land on the very same minute.
//
// The helpers here are the one place that math lives, so the live display, the
// roll editors' live preview and the Zmanim boards can never drift apart.

import { ZmanimDisplayOverrideRoundTo } from '@/utils/enums';

const MS_PER_MINUTE = 60000;
/** Midpoint for `round to closer`: >= :30 goes up, below it goes down. */
const CLOSER_MIDPOINT_MS = 30000;

/**
 * A stored `roundTo` value, or the organization-wide default (`round to
 * closer`) when it is missing/unrecognised — matching what the Zmanim display
 * falls back to for a zman that has no Time Settings saved yet.
 */
export function normalizeZmanimRoundTo(value: unknown): ZmanimDisplayOverrideRoundTo {
  return Object.values(ZmanimDisplayOverrideRoundTo).includes(value as ZmanimDisplayOverrideRoundTo)
    ? (value as ZmanimDisplayOverrideRoundTo)
    : ZmanimDisplayOverrideRoundTo.closer;
}

/**
 * The organization's "Round To" for one zman key, read off the saved
 * Zmanim_Display_Override_N rows (`GET_ZMANIM_DISPLAY_OVERRIDES_N`). Falls back
 * to `round to closer` when that zman has no Time Settings saved.
 */
export function getOrgZmanimRoundTo(
  overrides: any,
  zmanimKey: string,
): ZmanimDisplayOverrideRoundTo {
  if (!zmanimKey || !Array.isArray(overrides)) return ZmanimDisplayOverrideRoundTo.closer;
  const match = overrides.find((item) => item && item.zmanimKey === zmanimKey);
  return normalizeZmanimRoundTo(match?.roundTo);
}

/**
 * Fold a zman's seconds into a whole minute in the given direction, returning
 * the shifted ISO string (seconds/milliseconds are always zero on the result).
 *
 * The shift is applied to the absolute instant: every IANA zone offset is a
 * whole number of minutes, so the seconds-within-the-minute are the same in UTC
 * as on the organization's wall clock, and the rounded instant formats to the
 * intended local minute in any timezone.
 *
 * e.g. 6:42:10 → high 6:43, closer 6:42, lower 6:42
 *      6:42:30 → high 6:43, closer 6:43, lower 6:42
 */
export function roundZmanIsoToMinute(iso: any, roundTo?: unknown): string {
  if (!iso) return '';
  const date = iso instanceof Date ? new Date(iso.getTime()) : new Date(iso);
  if (Number.isNaN(date.getTime())) return '';

  const msIntoMinute = (date.getUTCSeconds() * 1000) + date.getUTCMilliseconds();
  if (msIntoMinute === 0) return date.toISOString();

  const direction = normalizeZmanimRoundTo(roundTo);
  let delta: number;
  if (direction === ZmanimDisplayOverrideRoundTo.high) {
    delta = MS_PER_MINUTE - msIntoMinute;
  } else if (direction === ZmanimDisplayOverrideRoundTo.lower) {
    delta = -msIntoMinute;
  } else {
    delta = msIntoMinute >= CLOSER_MIDPOINT_MS ? MS_PER_MINUTE - msIntoMinute : -msIntoMinute;
  }

  date.setTime(date.getTime() + delta);
  return date.toISOString();
}

/**
 * Apply the organization's Time Settings "Round To" for `zmanimKey` to a
 * resolved zman ISO. Used by every Minyanim Roll consumer of a Dynamic Zman /
 * Weekly Static Zman time, so a minyan shows the same minute the Zmanim board
 * shows for that zman.
 *
 * The Zmanim board's `showSeconds` is deliberately not consulted: a minyan has
 * its own Show Seconds toggle, and every caller skips this rounding while that
 * is on (isMinyanShowSecondsEnabled). Otherwise the seconds are always folded
 * in — using the direction the organization chose rather than truncating.
 *
 * Returns the input unchanged when it can't be parsed, so callers keep whatever
 * fallback handling they already have.
 */
export function applyOrgZmanimRoundingToIso(
  iso: any,
  zmanimKey: string,
  overrides: any,
): string {
  if (!iso || !zmanimKey) return iso || '';
  return roundZmanIsoToMinute(iso, getOrgZmanimRoundTo(overrides, zmanimKey)) || iso;
}
